import LoadingSpinner from '@/components/CustomComponent/LoadingSpinner';
import { COMMON_TEXT } from '@/helpers/common-text';
import { MeetingRoomUsageMarkerRender } from '@/helpers/meeting-room-usage-marker';
import { transformPositionToMarkerCoordinate } from '@/helpers/utility';

import { Message } from 'primereact/message';
import React, { Component, useRef, useState, useCallback, useEffect } from 'react';
import Draggable from 'react-draggable';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import '@/components/CustomComponent/ReactImageMarker/image-marker.less';
import { ZoomInIcon, ZoomOutIcon, ZoomResetIcon } from './icons';

const DEFAULT_BUFFER = 12;
const INITIAL_SCALE = 0.9;

// Custom ImageMarker component with external zoom control access
const CustomImageMarker = ({
  src,
  markers,
  onAddMarker,
  markerComponent: MarkerComponent,
  bufferLeft = DEFAULT_BUFFER,
  bufferTop = DEFAULT_BUFFER,
  alt = 'Markers',
  extraClass = '',
  isDisableDragMarker = true,
  onMarkerClicked,
  onTransformRefSet,
}) => {
  const imageRef = useRef(null);
  const imageMarkerContainerRef = useRef(null);
  const resetTransformRef = useRef(null);
  const [isDisablePan, setIsDisablePan] = useState(false);
  const [currentScaleValue, setCurrentScaleValue] = useState(INITIAL_SCALE);
  const [isImageCentered, setIsImageCentered] = useState(false);
  const [computedInitialScale, setComputedInitialScale] = useState(INITIAL_SCALE);

  const computeInitialScale = useCallback(() => {
    if (!imageRef.current || !imageMarkerContainerRef.current) {
      return INITIAL_SCALE;
    }

    const image = imageRef.current;
    const container = imageMarkerContainerRef.current;
    
    const imageWidth = image.naturalWidth;
    const imageHeight = image.naturalHeight;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;

    if (
      imageWidth === 0 ||
      imageHeight === 0 ||
      containerWidth === 0 ||
      containerHeight === 0
    ) {
      return INITIAL_SCALE;
    }

    // Calculate scale to fit image within container while maintaining aspect ratio
    const scaleX = containerWidth / imageWidth;
    const scaleY = containerHeight / imageHeight;

    const scale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond original size

    // Add some padding (10% margin)
    return Math.max(scale * 0.9, 0.8); // Ensure it's not smaller than 0.8
  }, []);

  const handleImageLoad = useCallback(() => {
    // Compute the appropriate initial scale based on image and container dimensions
    const newScale = computeInitialScale();
    setComputedInitialScale(newScale);
    
    setTimeout(() => {
      if (resetTransformRef.current && resetTransformRef.current.centerView) {
        // Set the scale and center the image
        resetTransformRef.current.centerView(newScale, 0);
      }
      setIsImageCentered(true);
    }, 100);
  }, [computeInitialScale]);

  const handleImageClick = useCallback(
    event => {
      if (!imageRef.current || !onAddMarker || !isImageCentered) {
        return;
      }
      const imageDimensions = imageRef.current.getBoundingClientRect();
      const rect = imageDimensions;
      const x = event.clientX - rect.left;
      const y = event.clientY - rect.top;
      
      const top = ((y - bufferTop) / rect.height) * 100;
      const left = ((x - bufferLeft) / rect.width) * 100;

      onAddMarker({
        top: Math.max(0, Math.min(100, top)),
        left: Math.max(0, Math.min(100, left)),
      });
    },
    [onAddMarker, bufferLeft, bufferTop, isImageCentered]
  );

  const markerOnClicked = useCallback(
    ({ data }) => {
      if (onMarkerClicked) {
        onMarkerClicked({ data });
      }
    },
    [onMarkerClicked]
  );

  const getItemPosition = marker => {
    const { top, left } = marker;
    return {
      top: `${top}%`,
      left: `${left}%`,
    };
  };

  const startDrag = useCallback(() => {
    setIsDisablePan(true);
  }, []);

  const stopDrag = useCallback(() => {
    setIsDisablePan(false);
  }, []);

  // Handle window resize to recalculate scale
  useEffect(() => {
    let resizeTimeout;
    
    const handleResize = () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      
      resizeTimeout = setTimeout(() => {
        if (imageRef.current && imageMarkerContainerRef.current && isImageCentered) {
          const newScale = computeInitialScale();
          setComputedInitialScale(newScale);
          
          // Reset transform with new scale
          if (resetTransformRef.current && resetTransformRef.current.centerView) {
            resetTransformRef.current.centerView(newScale, 0);
          }
        }
      }, 150);
    };

    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
    };
  }, [computeInitialScale, isImageCentered]);

  return (
    <TransformWrapper
      wheel={{ step: 0.1, disabled: true }}
      minScale={0.2}
      maxScale={4.0}
      initialScale={computedInitialScale}
      centerOnInit={true}
      limitToBounds={true}
      panning={{ disabled: isDisablePan }}
      onTransformed={(ref, event) => {
        setCurrentScaleValue(ref.state.scale);
      }}
    >
      {({ zoomIn, zoomOut, resetTransform, centerView, ...rest }) => {
        // Store transform functions in ref for use in image onLoad
        resetTransformRef.current = {
          zoomIn,
          zoomOut,
          resetTransform,
          centerView
        };

        // Pass transform functions to parent component
        if (onTransformRefSet) {
          onTransformRefSet({ 
            zoomIn, 
            zoomOut, 
            resetTransform,
            centerView,
            isImageCentered,
            computedInitialScale
          });
        }
        
        return (
          <TransformComponent
            wrapperStyle={{
              width: '100%',
              height: '100%',
            }}
          >
            <div 
              className="image-marker"
              ref={imageMarkerContainerRef}
              style={{ visibility: isImageCentered ? 'visible' : 'hidden' }}
            >
              <img
                src={src}
                alt={alt}
                onClick={handleImageClick}
                onLoad={handleImageLoad}
                className={`image-marker__image ${extraClass}`}
                ref={imageRef}
              />
              {isImageCentered && markers.map((marker, itemNumber) => (
                <Draggable
                  key={`draggable-marker-${itemNumber}`}
                  onStart={startDrag}
                  onStop={stopDrag}
                  disabled={isDisableDragMarker}
                  scale={currentScaleValue}
                >
                  <div
                    className={`image-marker__marker ${
                      !MarkerComponent && 'image-marker__marker--default'
                    }`}
                    key={itemNumber}
                    style={{ ...getItemPosition(marker), cursor: 'pointer' }}
                    data-testid="marker"
                  >
                    {MarkerComponent ? (
                      <MarkerComponent
                        {...marker}
                        itemNumber={itemNumber}
                        markerOnClicked={markerOnClicked}
                      />
                    ) : (
                      itemNumber + 1
                    )}
                  </div>
                </Draggable>
              ))}
            </div>
          </TransformComponent>
        );
      }}
    </TransformWrapper>
  );
};

class MeetingRoomUsageMap extends Component {
  constructor(props) {
    super(props);
    this.state = {
      screenWidth: window.innerWidth,
    };
    this.transformRef = null;
    this.lastScreenWidth = window.innerWidth;
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    const currentWidth = window.innerWidth;
    this.lastScreenWidth = currentWidth;
    this.setState({ screenWidth: currentWidth });
    // Note: Resize handling for image centering is now handled in the functional component
  };

  setTransformRef = (ref) => {
    this.transformRef = ref;
  };

  handleZoomIn = () => {
    if (this.transformRef && this.transformRef.isImageCentered) {
      this.transformRef.zoomIn();
    }
  };

  handleZoomOut = () => {
    if (this.transformRef && this.transformRef.isImageCentered) {
      this.transformRef.zoomOut();
    }
  };

  handleZoomReset = () => {
    // if (this.transformRef && this.transformRef.isImageCentered) {
    //   // Use centerView with computed initial scale for consistent reset
    //   if (this.transformRef.centerView && this.transformRef.computedInitialScale) {
    //     this.transformRef.centerView(this.transformRef.computedInitialScale, 0);
    //   } else if (this.transformRef.resetTransform) {
    //     this.transformRef.resetTransform();
    //   }
    // }
    if (this.transformRef) {
      this.transformRef.resetTransform();
    }
  };

  render() {
    const {
      data: floorMapData,
      isLoading,
      floorMapUrl,
      floorMapError,
      onMarkerClicked,
    } = this.props;

    const floorMapDataMarkers = floorMapData?.map(item => {
      const marker = transformPositionToMarkerCoordinate(item.position);
      return {
        ...marker,
        item: item,
      };
    });

    if (isLoading) {
      return (
        <div className="loading-container">
          <LoadingSpinner />
        </div>
      );
    }

    if (floorMapError) {
      return (
        <div className="error-container">
          <Message
            severity="error"
            text={COMMON_TEXT.CANNOT_LOAD_FLOORMAP}
          />
        </div>
      );
    }

    return (
      <div className="meeting-room-usage-map-container">
        <div className="image-container">
          <CustomImageMarker
            extraClass="custom-image-marker"
            bufferLeft={0}
            bufferTop={0}
            src={floorMapUrl}
            markerComponent={MeetingRoomUsageMarkerRender}
            onMarkerClicked={onMarkerClicked}
            markers={floorMapDataMarkers || []}
            alt=""
            onTransformRefSet={this.setTransformRef}
          />
        </div>
        
        <div className="zoom-controls">
          <button
            className="zoom-out"
            onClick={this.handleZoomOut}
            title="ズームアウト"
          >
            <ZoomOutIcon />
          </button>
          <button
            className="zoom-reset"
            onClick={this.handleZoomReset}
            title="リセット"
          >
            <ZoomResetIcon />
          </button>
          <button
            className="zoom-in"
            onClick={this.handleZoomIn}
            title="ズームイン"
          >
            <ZoomInIcon />
          </button>
        </div>
      </div>
    );
  }
}

export default MeetingRoomUsageMap; 
